import { useEffect, useState } from 'react';
import { onValue, ref, set, push, serverTimestamp } from 'firebase/database';
import { db } from '@/shared/firebase/config';
import { useAuth } from '@/shared/auth/AuthContext';
import { useCenterConfig } from '@/shared/hooks/useCenterConfig';
import {
  isNative, requestLocationPermission, watchPosition, vibrate,
} from '@/shared/native';
import type { TrackedConfig, TrackedLatest } from '@/shared/firebase/types';

const SEND_INTERVAL_MS = 30_000;
const HISTORY_INTERVAL_MS = 5 * 60_000;

const NAME_KEY = 'gc_usuario_name';
const LEGACY_NAME_KEYS = ['gc_cuidado_name', 'gc_residente_name'];

function loadInitialName(): string {
  const fresh = localStorage.getItem(NAME_KEY);
  if (fresh) return fresh;
  for (const k of LEGACY_NAME_KEYS) {
    const v = localStorage.getItem(k);
    if (v) return v;
  }
  return '';
}

export default function UsuarioApp() {
  const { centerId, accessCode, user, logout } = useAuth();
  const center = useCenterConfig(centerId);

  // Solo un código de rol "usuario" puede emitir GPS. Admin con email entra en modo diagnóstico.
  const isAdminPreview = !accessCode && !!user?.email;
  const uid = accessCode?.role === 'usuario' ? accessCode.residentUid : null;

  const [name, setName] = useState<string>(loadInitialName());
  const [profile, setProfile] = useState<TrackedConfig | null>(null);

  useEffect(() => {
    if (!centerId || !uid) return;
    return onValue(ref(db, `centers/${centerId}/tracked/${uid}/config`), (snap) => {
      const cfg = (snap.val() as TrackedConfig | null) || null;
      setProfile(cfg);
      if (cfg?.name) {
        setName(cfg.name);
        localStorage.setItem(NAME_KEY, cfg.name);
      }
    });
  }, [centerId, uid]);

  if (isAdminPreview) {
    return (
      <div className="min-h-full grid place-items-center px-4">
        <div className="card max-w-md text-center">
          <p className="text-sm text-slate-700 mb-3">Vista de usuario (modo diagnóstico).</p>
          <p className="text-xs text-slate-500 mb-5">
            Estás conectado como administrador. El envío de GPS está desactivado para no contaminar los datos reales.
            Para probar GPS y SOS, entra con un código de rol "usuario".
          </p>
          <button onClick={() => history.back()} className="btn-primary w-full">Volver</button>
        </div>
      </div>
    );
  }

  if (!uid) {
    return (
      <div className="min-h-full grid place-items-center px-4">
        <div className="card max-w-md text-center">
          <p className="text-sm text-slate-700 mb-3">Este código no tiene a nadie asignado.</p>
          <p className="text-xs text-slate-500 mb-5">Pídele al administrador un código nuevo de rol "usuario" vinculado a tu ficha.</p>
          <button onClick={logout} className="btn-primary w-full">Salir</button>
        </div>
      </div>
    );
  }

  return (
    <UsuarioHome
      centerId={centerId}
      uid={uid}
      name={name}
      profile={profile}
      centerName={center?.name || centerId}
      centerPhone={center?.phone}
      onLogout={logout}
    />
  );
}

type LiveStatus = 'init' | 'no-permission' | 'sending' | 'error';

function UsuarioHome({
  centerId, uid, name, profile, centerName, centerPhone, onLogout,
}: {
  centerId: string; uid: string; name: string;
  profile: TrackedConfig | null;
  centerName: string; centerPhone?: string;
  onLogout: () => Promise<void>;
}) {
  const [status, setStatus] = useState<LiveStatus>('init');
  const [lastSent, setLastSent] = useState<number | null>(null);
  const [coords, setCoords] = useState<{ lat: number; lng: number; acc?: number } | null>(null);
  const [sosSent, setSosSent] = useState(false);
  const [sosError, setSosError] = useState<string | null>(null);
  const [latest, setLatest] = useState<TrackedLatest | null>(null);

  // Suscripción a latest (para que vea su propia última señal incluso si vino de otro dispositivo)
  useEffect(() => {
    return onValue(ref(db, `centers/${centerId}/tracked/${uid}/latest`), (snap) => {
      setLatest((snap.val() as TrackedLatest | null) || null);
    });
  }, [centerId, uid]);

  useEffect(() => {
    let stopWatch: (() => void) | null = null;
    let lastLatestMs = 0;
    let lastHistoryMs = 0;

    (async () => {
      const ok = await requestLocationPermission();
      if (!ok) { setStatus('no-permission'); return; }
      try {
        stopWatch = await watchPosition(async (p) => {
          const lat = p.coords.latitude;
          const lng = p.coords.longitude;
          const acc = p.coords.accuracy;
          setCoords({ lat, lng, acc });
          const now = Date.now();

          if (now - lastLatestMs >= SEND_INTERVAL_MS) {
            lastLatestMs = now;
            try {
              await set(ref(db, `centers/${centerId}/tracked/${uid}/latest`), {
                lat, lng, ts: now, accuracy: acc,
              });
              setLastSent(now);
              setStatus('sending');
            } catch (e) { setStatus('error'); console.error(e); }
          }
          if (now - lastHistoryMs >= HISTORY_INTERVAL_MS) {
            lastHistoryMs = now;
            try {
              await push(ref(db, `centers/${centerId}/tracked/${uid}/locations`), { lat, lng, ts: now, accuracy: acc });
            } catch (e) { console.error(e); }
          }
        });
      } catch (e) { setStatus('error'); console.error(e); }
    })();

    return () => { if (stopWatch) stopWatch(); };
  }, [centerId, uid]);

  async function triggerSos() {
    if (sosSent) return;
    setSosError(null);
    setSosSent(true);
    try { await vibrate('heavy'); } catch (e) { console.warn(e); }
    const now = Date.now();
    const sosAlert = {
      ts: now, type: 'sos', uid,
      message: `${name || uid} ha pulsado SOS`,
      acknowledged: false,
      lat: coords?.lat ?? null,
      lng: coords?.lng ?? null,
    };
    try {
      await push(ref(db, `centers/${centerId}/tracked/${uid}/alerts`), sosAlert);
      await push(ref(db, `centers/${centerId}/alerts`), sosAlert);
      await set(ref(db, `centers/${centerId}/tracked/${uid}/lastSosTs`), serverTimestamp());
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setSosError(msg);
      setSosSent(false);
      window.alert('No se pudo enviar el SOS:\n' + msg);
      return;
    }
    setTimeout(() => setSosSent(false), 8000);
  }

  const hasGeofence = !!profile?.geofence;
  const battery = coords ? null : latest?.battery; // si tenemos GPS directo no mostramos batería del dispositivo
  const lastTs = lastSent || latest?.ts;

  return (
    <div className="min-h-full flex flex-col bg-gradient-to-b from-brand-50 to-white">
      <header className="px-5 py-4 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-2xl font-extrabold text-slate-900 truncate">Hola, {name || uid}</p>
          <p className="text-xs text-slate-500">{centerName}</p>
        </div>
      </header>

      <main className="flex-1 px-5 pb-6 flex flex-col gap-4">
        <div className="card text-center">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Estado</p>
          <p className={`text-xl font-extrabold ${
            status === 'sending' ? 'text-success' :
            status === 'no-permission' ? 'text-danger' :
            status === 'error' ? 'text-warning' : 'text-slate-500'
          }`}>
            {status === 'init' && 'Iniciando GPS…'}
            {status === 'no-permission' && 'Permiso de ubicación denegado'}
            {status === 'sending' && '✓ Conectado'}
            {status === 'error' && 'Error de envío'}
          </p>

          <ul className="mt-3 text-sm text-slate-700 space-y-1">
            {lastTs && (
              <li className="flex justify-between">
                <span className="text-slate-500">Última señal</span>
                <span>{new Date(lastTs).toLocaleTimeString()}</span>
              </li>
            )}
            {coords && (
              <>
                <li className="flex justify-between">
                  <span className="text-slate-500">Coordenadas</span>
                  <span>{coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}</span>
                </li>
                {coords.acc != null && (
                  <li className="flex justify-between">
                    <span className="text-slate-500">Precisión</span>
                    <span>±{Math.round(coords.acc)} m</span>
                  </li>
                )}
              </>
            )}
            {battery != null && (
              <li className="flex justify-between">
                <span className="text-slate-500">Batería</span>
                <span>{Math.round(battery)}%</span>
              </li>
            )}
            {hasGeofence && (
              <li className="flex justify-between">
                <span className="text-slate-500">Zona segura</span>
                <span>radio {profile!.geofence!.radius} m</span>
              </li>
            )}
          </ul>

          {!isNative && <p className="text-xs text-slate-400 mt-2">(Modo navegador)</p>}
        </div>

        <button
          onClick={triggerSos}
          disabled={sosSent}
          className={`flex-1 min-h-[40vh] rounded-3xl shadow-lift text-white font-extrabold transition active:scale-95
            ${sosSent ? 'bg-danger-dark animate-pulse' : 'bg-danger hover:bg-danger-dark'}`}
        >
          <div className="text-7xl mb-4">🆘</div>
          <div className="text-3xl">{sosSent ? 'SOS ENVIADO' : 'SOS'}</div>
          <div className="text-sm font-medium opacity-90 mt-2">
            {sosSent ? 'La residencia ha sido avisada' : 'Pulsa si necesitas ayuda'}
          </div>
        </button>

        {sosError && (
          <div className="rounded-xl bg-danger-50 text-danger text-xs p-3 break-words">
            <b>Error:</b> {sosError}
          </div>
        )}

        {centerPhone && (
          <a href={`tel:${centerPhone}`} className="btn-ghost text-center">
            📞 Llamar a la residencia
          </a>
        )}

        <button onClick={onLogout} className="btn-ghost text-xs">Salir</button>
      </main>
    </div>
  );
}
